import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"
import { reorderWithEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge"
import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--tag-input-sortable"
//
// Opt-in drag-and-drop reordering for Ui::TagInputComponent (`sortable: true`),
// for the fields where the order carries meaning — a contact's first email and
// phone are its primary ones. Uses the same @atlaskit engine as
// ui--draggable-list, so it behaves like the React tag input rather than a
// SortableJS approximation.
//
// A <select multiple> submits its SELECTED OPTIONS IN DOM ORDER, not in the order
// they were picked. So a reorder is only real once the <option> nodes themselves
// are reordered — moving the visible chip alone would look right and submit the
// old order. #applyOrder does both, and Tom Select's own `items` array is kept in
// step so it does not re-render the stale order on its next refresh.
const ITEM_TYPE = "ui-tag-input-chip"
const CHIP_SELECTOR = ".ts-control .item"
const HANDLE_CLASS = "ui-tag-grip"

// The `grip_vertical` glyph (app/assets/config/icons.json), inlined because Tom
// Select builds the chips in JS — there is no ERB pass to render an icon into.
// Same six dots the draggable list uses for its handle.
const GRIP_SVG = `<svg class="${HANDLE_CLASS} shrink-0 cursor-grab" width="14" height="14" viewBox="0 0 24 24"
  fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
  aria-hidden="true" focusable="false"><circle cx="9" cy="5" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="9" cy="19" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="19" r="1"/></svg>`

export default class extends Controller {
  static targets = ["select"]

  connect() {
    this._chipCleanups = new Map()
    this._cleanups = [
      monitorForElements({
        canMonitor: ({ source }) => source.data.type === ITEM_TYPE,
        onDrop: ({ source, location }) => this._onDrop(source, location),
      }),
    ]

    // Tom Select renders the chips, so wire them up after it has, and again
    // whenever the set changes (adding or removing a tag re-renders them).
    this._observer = new MutationObserver(() => this._wireChips())
    this._observer.observe(this.element, { childList: true, subtree: true })
    this._wireChips()
  }

  disconnect() {
    this._observer?.disconnect()
    this._cleanups.forEach((fn) => fn())
    this._chipCleanups.forEach((fn) => fn())
    this._chipCleanups.clear()
  }

  get tomSelect() {
    return this.hasSelectTarget ? this.selectTarget.tomselect : null
  }

  _chips() {
    return [...this.element.querySelectorAll(CHIP_SELECTOR)]
  }

  _wireChips() {
    const chips = this._chips()

    // Drop the wiring of chips that are gone, so the map cannot grow unbounded
    // as tags are added and removed.
    this._chipCleanups.forEach((cleanup, chip) => {
      if (chips.includes(chip)) return
      cleanup()
      this._chipCleanups.delete(chip)
    })

    chips.forEach((chip) => {
      if (this._chipCleanups.has(chip)) return

      const value = chip.dataset.value
      // A pinned chip (data-locked, stamped by ui--select) gets no grip and no
      // drag, and only takes a drop on its right edge — so nothing can land in
      // front of it and it stays where it is: position 0, the primary email.
      const locked = this._isLocked(chip)

      const cleanupDraggable = locked
        ? null
        : draggable({
            element: chip,
            // Dragging starts from the grip, so clicking the chip body (or its
            // remove button) still behaves normally.
            dragHandle: this._ensureHandle(chip),
            getInitialData: () => ({ type: ITEM_TYPE, value }),
            onDragStart: () => chip.classList.add("opacity-40"),
            onDrop: () => chip.classList.remove("opacity-40"),
          })

      const cleanupDropTarget = dropTargetForElements({
        element: chip,
        getIsSticky: () => true,
        canDrop: ({ source }) => source.data.type === ITEM_TYPE,
        getData: ({ element, input }) =>
          attachClosestEdge(
            { type: ITEM_TYPE, value },
            // Chips flow horizontally, so the insertion point is left/right.
            {
              element,
              input,
              allowedEdges: locked ? ["right"] : ["left", "right"],
            },
          ),
        onDragEnter: ({ source, self }) =>
          source.element === chip
            ? null
            : this._paintEdge(chip, extractClosestEdge(self.data)),
        onDrag: ({ source, self }) =>
          source.element === chip
            ? null
            : this._paintEdge(chip, extractClosestEdge(self.data)),
        onDragLeave: () => this._clearEdge(chip),
        onDrop: () => this._clearEdge(chip),
      })

      this._chipCleanups.set(chip, () => {
        cleanupDraggable?.()
        cleanupDropTarget()
      })
    })
  }

  // Prepend the six-dot grip that signals the chip can be dragged, mirroring the
  // React tag input. Idempotent: Tom Select re-renders chips on every change.
  _ensureHandle(chip) {
    const existing = chip.querySelector(`.${HANDLE_CLASS}`)
    if (existing) return existing

    chip.insertAdjacentHTML("afterbegin", GRIP_SVG)
    chip.classList.add("gap-1")
    const handle = chip.querySelector(`.${HANDLE_CLASS}`)

    // Tom Select's control calls preventDefault() on mousedown to hold focus,
    // which stops the browser ever starting a drag. Keep the grip's mousedown
    // from reaching it so the drag can begin.
    handle.addEventListener("mousedown", (event) => event.stopPropagation())

    return handle
  }

  _onDrop(source, location) {
    const target = location.current.dropTargets[0]
    this._chips().forEach((chip) => this._clearEdge(chip))
    if (!target) return

    const values = this._chips().map((chip) => chip.dataset.value)
    const from = values.indexOf(source.data.value)
    const to = values.indexOf(target.data.value)
    if (from === -1 || to === -1 || from === to) return

    const reordered = reorderWithEdge({
      list: values,
      startIndex: from,
      indexOfTarget: to,
      closestEdgeOfTarget: extractClosestEdge(target.data),
      axis: "horizontal",
    })

    // The edge rule above should already make this unreachable; the server
    // refuses a moved primary anyway, so do not let the chips lie about it.
    const lockedMoved = this._chips().some(
      (chip) =>
        this._isLocked(chip) &&
        reordered.indexOf(chip.dataset.value) !==
          values.indexOf(chip.dataset.value),
    )
    if (lockedMoved) return

    this._applyOrder(reordered)
    this.dispatch("reorder", { detail: { values: reordered } })
  }

  _isLocked(chip) {
    return chip.dataset.locked === "true"
  }

  // Reorder the <option> nodes (what gets submitted), then the chips, then Tom
  // Select's own bookkeeping — all three have to agree.
  _applyOrder(values) {
    // Values are arbitrary user text (a phone is "+48 22 000 0009"), so the nodes
    // are matched by comparing values rather than by building an attribute
    // selector — CSS.escape is for identifiers and mangles a quoted value.
    const byValue = (nodes, value, read) =>
      nodes.find((node) => read(node) === value)

    const select = this.hasSelectTarget ? this.selectTarget : null
    if (select) {
      const options = [...select.options]
      values.forEach((value) => {
        const option = byValue(options, value, (o) => o.value)
        if (option) select.appendChild(option)
      })
    }

    const control = this.element.querySelector(".ts-control")
    if (control) {
      // Tom Select keeps its text input as the last child of the control, so the
      // chips are inserted BEFORE it — appending would push them past the caret.
      const input = control.querySelector("input")
      const chips = [...control.querySelectorAll(".item")]
      values.forEach((value) => {
        const chip = byValue(chips, value, (c) => c.dataset.value)
        if (!chip) return

        if (input) {
          control.insertBefore(chip, input)
        } else {
          control.appendChild(chip)
        }
      })
    }

    const instance = this.tomSelect
    if (instance) instance.items = [...values]
  }

  // A ring on the chip the drop would land against. The edge itself is not
  // painted: chips are small and a 2px side-marker reads as a rendering glitch.
  _paintEdge(chip, edge) {
    if (!edge) return this._clearEdge(chip)

    chip.classList.add("ring-primary", "ring-2")
  }

  _clearEdge(chip) {
    chip.classList.remove("ring-primary", "ring-2")
  }
}
